"use client";

import { useCallback, useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import ControlledTable from "@/components/controlled-table";
import { useTableServer } from "@/hooks/useTableServer";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import { IProject } from "@/utilities/types/Project";
import ProjectRepository from "@/utilities/repositories/Project";
import { Roles } from "@/utilities/types/Users";
import toast from "react-hot-toast";
import { useModal } from "@/app/shared/modal-views/use-modal";
import DisclaimerNotification from "../../disclaimer/disclaimer-notifcation";
import useProfile from "@/hooks/use-profile";
import { AGENCY_PROJECT_MODAL_SHOWN_KEY } from "@/config/constants";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getColumnsAgentProject } from "./columns-agent-project";
import { Title } from "rizzui";
import { backPrev } from "@/utilities/functions";
import { routes } from "@/config/routes";
const FilterElement = dynamic(
  () => import("@/app/app-shared/project/project-table/filter-element"),
  { ssr: false }
);

export default function AgentProjectsTable() {
  const onHeaderCellClick = (value: string) => ({
    onClick: () => {
      handleSort(value);
    },
  });

  const { profile } = useProfile();
  const navigate = useNavigate();

  const routerState = useLocation().state;

  if (!routerState?.location) {
    navigate(-1);
    return <></>;
  }

  const { request: toggleShowProject } = useApi<IResponseData<IProject>>({
    request: ProjectRepository.updateUserProject,
    enableToast: true,
  });

  const {
    isLoading,
    isFiltered,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
    handleChangePageSize,
    filters,
    updateFilter,
    searchTerm,
    handleSearch,
    sortConfig,
    handleSort,
    handleReset,
  } = useTableServer({
    server: {
      request: ProjectRepository.getAgentProject,
    },
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return {
        search: s,
      };
    },
    initialFilterState: {
      locationIds: [routerState?.location?.id],
    },
  });
  const onToggleShowProject = useCallback(
    async (
      id: string | undefined,
      data: {
        isShow?: boolean;
        promotion?: boolean;
        featured?: boolean;
      },
      onSuccess: () => void
    ) => {
      await toggleShowProject(id, data)
        .then(() => {
          // handleReset(); // Recall API
          onSuccess();
        })
        .catch((err: any) => {
          toast.error(err?.message || "Something went wrong");
        });
    },
    [handleReset]
  );

  const columns = useMemo(
    () =>
      getColumnsAgentProject({
        data: tableData,
        sortConfig,
        onHeaderCellClick,
        role: Roles.AGENCY,
        onToggleShowProject,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onHeaderCellClick,
      sortConfig.key,
      sortConfig.direction,
      tableData,
      onToggleShowProject,
    ]
  );

  // const { visibleColumns } = useColumn(columns);
  //TODO: Uncomment filter and test when BE already filter

  const { openModal } = useModal();

  useEffect(() => {
    const modalShown = Boolean(
      sessionStorage.getItem(AGENCY_PROJECT_MODAL_SHOWN_KEY)
    );
    if (profile?.role === Roles.AGENCY && !modalShown) {
      openModal({
        view: <DisclaimerNotification />,
        customSize: "500px",
      });
      sessionStorage.setItem(AGENCY_PROJECT_MODAL_SHOWN_KEY, "true");
    }
  }, [profile?.role]);

  return (
    <div className="mt-4">
      <div className="mb-4 w-full flex items-center justify-between">
        <Title as="h5">
          <strong>{routerState?.location?.name}</strong>
        </Title>
        <Link
          to={backPrev(routes.agentLocation.listing)}
          className={"mr-2 ml-auto"}
        >
          <span
            className={
              "px-4 py-2 inline-block border border-solid border-black rounded-md"
            }
          >
            Back
          </span>
        </Link>
      </div>
      <ControlledTable
        variant="modern"
        data={tableData}
        isLoading={isLoading}
        showLoadingText={true}
        // @ts-ignore
        columns={columns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />
    </div>
  );
}
